/**
 * スコア管理画面
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getScores, deleteScore, getPublicRankingSample } from '../../lib/admin-api';
import { DEMO_TEACHER_BASE, demoEvaluationHref, getPublicTeacherScores } from '../../lib/demo-teacher';
import { useLocation } from 'wouter';
import { AuthService } from '../../lib/auth';
import type { StaffRole } from '../../types';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2, Eye, Trash2, TrendingUp, ArrowDown, ArrowUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

type ScoreRow = {
  interview_id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  lecture_title?: string | null;
  overall_score_value?: number | null;
  overall_score_total?: number | null;
  overall_score_max?: number | null;
  patient_score_value?: number | null;
  dentist_score_value?: number | null;
  patient_score_id?: number | null;
  dentist_score_id?: number | null;
  /** 公開デモのみ */
  participant_code?: string;
  created: string;
};

/** 教員画面の公開デモ（/demo/teacher/scores）。読み取り専用 */
export function DemoTeacherScores() {
  return <Scores demo />;
}

export default function Scores({ demo = false }: { demo?: boolean } = {}) {
  const [, setLocation] = useLocation();
  const [page, setPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ScoreRow | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => AuthService.getInstance().getCurrentUser(),
    staleTime: 60 * 1000,
    enabled: !demo,
  });
  const role = me?.role as StaffRole | undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: demo ? ['public', 'teacher-demo', 'scores', page] : ['admin', 'scores', page, role],
    queryFn: () => (demo ? getPublicTeacherScores(page, 20) : getScores({ page, limit: 20 })),
  });

  // デモの名前は、ランキングと同じ出どころ（公開ランキング）から参加者番号で引いてそろえる
  const { data: demoRanking } = useQuery({
    queryKey: ['public', 'ranking-sample', 0, null, null],
    queryFn: () => getPublicRankingSample(0),
    enabled: demo,
    staleTime: 60 * 1000,
  });
  const demoNameByCode = new Map(
    (demoRanking?.students ?? []).map((student) => [student.participantCode ?? String(student.studentId), student.name]),
  );
  const displayName = (row: ScoreRow) =>
    (demo && row.participant_code ? demoNameByCode.get(row.participant_code) : undefined) ?? row.user_name ?? '-';

  const canDeleteScores = !demo && (role === 'admin' || role === 'student');

  const deleteMutation = useMutation({
    mutationFn: async (row: ScoreRow) => {
      const ids = [row.patient_score_id, row.dentist_score_id].filter((id): id is number => id != null);
      for (const id of ids) {
        await deleteScore(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scores'] });
      setIsDeleteDialogOpen(false);
      setSelectedRow(null);
      toast({
        title: '成功',
        description: 'スコアを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'スコアの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleView = (row: ScoreRow) => {
    if (demo && row.participant_code) {
      setLocation(demoEvaluationHref(row.interview_id, row.participant_code, `${DEMO_TEACHER_BASE}/scores`));
      return;
    }
    window.location.href = `/evaluation?interview_id=${row.interview_id}`;
  };

  const handleDelete = (row: ScoreRow) => {
    setSelectedRow(row);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedRow) {
      deleteMutation.mutate(selectedRow);
    }
  };

  return (
    <AdminLayout demo={demo}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">{demo ? '面接評価一覧' : 'スコア管理'}</h1>
          <p className="text-base text-gray-500 mt-2">
            {demo && '担当学生（負荷テストの参加者）の面接評価を確認できます。平均・最小・最大は患者面接のスコアです。'}
            {!demo && role === 'student' && 'ログインユーザー（あなた）のスコアのみ表示・削除できます。'}
            {!demo && role === 'educator' && 'あなたが担当する学生（紐づけ済み）のスコアを確認できます。'}
            {!demo && (role === 'admin' || !role) && 'すべてのユーザーのスコアを管理できます。'}
          </p>
        </div>

        {/* 統計情報 */}
        {data?.statistics && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-2xl font-bold text-gray-900 mb-4">統計情報</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x sm:divide-gray-200">
              <ScoreStatCell
                label={demo ? '平均患者スコア' : '平均総合スコア'}
                value={data.statistics.avg?.toFixed(1) || '0.0'}
                icon={<TrendingUp className="h-5 w-5" />}
                tone="cyan"
              />
              <ScoreStatCell
                label={demo ? '最小患者スコア' : '最小総合スコア'}
                value={String(data.statistics.min ?? 0)}
                icon={<ArrowDown className="h-5 w-5" />}
                tone="rose"
              />
              <ScoreStatCell
                label={demo ? '最大患者スコア' : '最大総合スコア'}
                value={String(data.statistics.max ?? 0)}
                icon={<ArrowUp className="h-5 w-5" />}
                tone="emerald"
              />
            </div>
          </div>
        )}

        {/* スコア一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">スコア一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ユーザー</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">講座</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">面接ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">総合スコア</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">患者スコア</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">歯科医スコア</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.scores?.map((row: ScoreRow) => (
                    <TableRow key={row.interview_id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <TableCell className="py-4">
                        <div>
                          <div className="text-base font-medium text-gray-900">{displayName(row)}</div>
                          <div className="text-base text-gray-500">
                            {demo ? (row.participant_code ? `学籍番号 ${row.participant_code}` : '-') : row.user_email || '-'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900">
                        <div className="min-w-40 max-w-72 whitespace-normal break-words">{row.lecture_title || '講座不明'}</div>
                      </TableCell>
                      <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">{row.interview_id}</TableCell>
                      <TableCell className="py-4 text-center text-xl font-extrabold text-gray-900 tabular-nums">
                        {row.overall_score_value != null ? row.overall_score_value : '-'}
                      </TableCell>
                      <TableCell className="py-4 text-center text-xl font-extrabold text-cyan-700 tabular-nums">
                        {row.patient_score_value != null ? row.patient_score_value : '-'}
                      </TableCell>
                      <TableCell className="py-4 text-center text-xl font-extrabold text-violet-700 tabular-nums">
                        {row.dentist_score_value != null ? row.dentist_score_value : '-'}
                      </TableCell>
                      <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">
                        {new Date(row.created).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={() => handleView(row)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50"
                            aria-label="詳細"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canDeleteScores && (
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                              aria-label="削除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>スコアを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。面接ID「{selectedRow?.interview_id}」の患者・歯科医スコアを削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

function ScoreStatCell({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  tone: 'cyan' | 'rose' | 'emerald';
}) {
  const toneIconCls =
    tone === 'cyan'
      ? 'bg-cyan-50 text-cyan-600'
      : tone === 'rose'
        ? 'bg-rose-50 text-rose-600'
        : 'bg-emerald-50 text-emerald-600';
  const toneValueCls = tone === 'cyan' ? 'text-cyan-700' : 'text-gray-900';
  return (
    <div className="px-6 py-2 first:pl-0 last:pr-0 flex flex-col items-center text-center">
      <div className="flex items-center gap-3">
        <div className="text-base font-bold text-gray-700">{label}</div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${toneIconCls}`}>
          {icon}
        </div>
      </div>
      <div className={`mt-4 text-5xl font-extrabold tracking-tight tabular-nums ${toneValueCls}`}>
        {value}
      </div>
    </div>
  );
}
